import { useState } from "react";
import { motion } from "framer-motion";
import { Star, PaperPlaneRight, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";
import { api, formatApiError } from "../lib/api";
import { useEffect } from "react";

const INITIAL = { name: "", location: "", brand: "", outcome: "", quote: "", rating: 5, email: "" };

function Stars({ rating, size = 15 }) {
  return (
    <span className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={size} weight={i < rating ? "fill" : "regular"} className={i < rating ? "text-accent" : "text-border"} />
      ))}
    </span>
  );
}

export default function Reviews() {
  const [reviews, setReviews] = useState(null);
  const [form, setForm] = useState(INITIAL);
  const [showForm, setShowForm] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => setReviews([]));
    const params = new URLSearchParams(window.location.search);
    if (params.get("review") === "1") {
      setForm((f) => ({
        ...f,
        name: params.get("name") || "",
        email: params.get("email") || "",
      }));
      setShowForm(true);
      setTimeout(() => {
        document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth" });
      }, 400);
    }
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/reviews", form);
      setSent(true);
      setForm(INITIAL);
      toast.success("Thank you — your outcome is with us for review.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-border rounded-sm px-4 py-3 text-sm font-medium text-foreground placeholder:text-foreground/35 focus:outline-none focus:border-primary transition-colors duration-300";

  return (
    <section id="reviews" data-testid="reviews-section" className="py-24 md:py-36 border-t border-border">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <Eyebrow>Owner Outcomes</Eyebrow>
            <h2 className="font-serif text-primary tracking-tight text-4xl sm:text-5xl mt-6 leading-[1.08]" data-testid="reviews-heading">
              The result is <em className="text-accent">the review</em>
            </h2>
          </div>
          <p className="text-foreground/60 max-w-sm text-sm md:text-base md:text-right">
            Every story is a real client outcome, shared with permission and published after review.
          </p>
        </Reveal>

        {reviews && reviews.length > 0 && (
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="reviews-grid">
            {reviews.map((r, i) => (
              <Reveal key={r.id} delay={(i % 3) * 0.1} className="h-full">
                <div className="h-full bg-card border border-border rounded-sm p-8 flex flex-col hover:border-accent/60 hover:-translate-y-1 transition-[border-color,transform] duration-500" data-testid={`review-card-${r.id}`}>
                  <Stars rating={r.rating} />
                  <p className="font-serif text-2xl text-primary tracking-tight mt-5 leading-snug" data-testid={`review-outcome-${r.id}`}>
                    {r.outcome}
                  </p>
                  <p className="mt-4 text-sm text-foreground/70 leading-relaxed flex-1">"{r.quote}"</p>
                  <p className="mt-6 pt-5 border-t border-border text-xs font-semibold text-foreground/50">
                    {r.name}{r.location ? ` · ${r.location}` : ""}{r.brand ? ` · ${r.brand} owner` : ""}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        )}

        <Reveal delay={0.15} className="mt-14">
          <div className="bg-secondary/50 border border-border rounded-sm p-8 md:p-10 max-w-3xl" data-testid="review-submit-card">
            {!showForm && !sent && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
                <div>
                  <p className="font-serif text-2xl text-primary tracking-tight">Worked with us?</p>
                  <p className="mt-2 text-sm text-foreground/60">Your outcome helps the next owner decide. Two minutes, honest words.</p>
                </div>
                <button
                  data-testid="review-open-form-button"
                  onClick={() => setShowForm(true)}
                  className="shrink-0 bg-primary text-primary-foreground px-7 py-3.5 rounded-full font-semibold text-sm hover:bg-accent transition-colors duration-300"
                >
                  Share your outcome
                </button>
              </div>
            )}
            {sent && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-3 text-sm font-semibold text-primary" data-testid="review-success">
                <CheckCircle size={22} weight="fill" /> Thank you — it will appear here after a quick review.
              </motion.p>
            )}
            {showForm && !sent && (
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="review-form">
                <input data-testid="review-name" required placeholder="First name & last initial *" value={form.name} onChange={set("name")} className={inputCls} />
                <div>
                  <input data-testid="review-email" type="email" placeholder="Email (never published)" value={form.email} onChange={set("email")} className={inputCls} />
                  <p className="mt-1.5 text-[11px] text-foreground/40 font-medium">Only used to verify your consultation — never shown.</p>
                </div>
                <input data-testid="review-location" placeholder="State (e.g. Arizona)" value={form.location} onChange={set("location")} className={inputCls} />
                <input data-testid="review-brand" placeholder="Ownership brand (e.g. Wyndham)" value={form.brand} onChange={set("brand")} className={inputCls} />
                <input data-testid="review-outcome" required placeholder="The outcome, in a few words * — e.g. Sold in 6 weeks" value={form.outcome} onChange={set("outcome")} className={`${inputCls} sm:col-span-2`} />
                <textarea data-testid="review-quote" required rows={3} placeholder="What happened, in your own words *" value={form.quote} onChange={set("quote")} className={`${inputCls} sm:col-span-2 resize-none`} />
                <div className="flex items-center gap-3" data-testid="review-rating">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Rating</span>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" data-testid={`review-star-${n}`} onClick={() => setForm((f) => ({ ...f, rating: n }))}>
                      <Star size={22} weight={n <= form.rating ? "fill" : "regular"} className={n <= form.rating ? "text-accent" : "text-border"} />
                    </button>
                  ))}
                </div>
                <div className="flex items-center justify-end gap-4">
                  <button type="button" data-testid="review-cancel" onClick={() => setShowForm(false)} className="text-sm font-semibold text-muted-foreground hover:text-primary transition-colors duration-300">
                    Cancel
                  </button>
                  <button
                    data-testid="review-submit-button"
                    type="submit"
                    disabled={sending}
                    className="flex items-center gap-2 bg-primary text-primary-foreground px-7 py-3.5 rounded-full font-semibold text-sm hover:bg-accent disabled:opacity-50 transition-colors duration-300"
                  >
                    {sending ? "Sending…" : "Submit for review"}
                    <PaperPlaneRight size={15} weight="bold" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
